<template>
  <div class="el-select" @click.stop="toggleMenu" v-click-outside="handleClose">
    <div class="el-select__tags" ref="tags" :style="{ 'max-width': inputWidth - 32 + 'px', width: '100%' }">
      <transition-group @after-leave="resetInputHeight">
        <el-tag
          v-for="item in selected"
          :key="item"
          closable
          size="small"
          type="info"
          @close="deleteTag($event, item)"
          disable-transitions
          v-if="item !== '其他'"
        >
          <span class="el-select__tags-text">{{ item.replace('其他', '') }}</span>
        </el-tag>
      </transition-group>
    </div>
    <el-input
      ref="reference"
      type="text"
      :placeholder="currentPlaceholder"
      :name="name"
      :id="id"
      :readonly="true"
      :validate-event="false"
      class="history-input"
      :class="{ 'is-focus': visible }"
      tabindex="-1"
      @focus.stop="handleFocus"
      @blur="handleBlur"
      @keydown.native.esc.stop.prevent="visible = false"
      @keydown.native.tab="visible = false"
      @mouseenter.native="inputHovering = true"
      @mouseleave.native="inputHovering = false"
    >
      <template slot="prefix" v-if="$slots.prefix">
        <slot name="prefix"></slot>
      </template>
      <template slot="suffix">
        <i v-show="!showClose" :class="['el-select__caret', 'el-input__icon', 'el-icon-' + iconClass]"></i>
      </template>
    </el-input>
    <transition name="el-zoom-in-top" @before-enter="handleMenuEnter" @after-leave="doDestroy">
      <select-menu ref="popper" :append-to-body="true" v-show="visible">
        <el-scrollbar
          tag="ul"
          wrap-class="el-select-dropdown__wrap"
          view-class="el-select-dropdown__list"
          ref="scrollbar"
          v-show="options.length > 0"
        >
          <slot></slot>
        </el-scrollbar>
      </select-menu>
    </transition>
  </div>
</template>

<script>
import Emitter from 'element-ui/src/mixins/emitter';
import clickOutside from 'element-ui/src/utils/clickoutside';
import scrollIntoView from 'element-ui/src/utils/scroll-into-view';
import { valueEquals } from 'element-ui/src/utils/util';
import { addResizeListener, removeResizeListener } from 'element-ui/src/utils/resize-event';
import selectMenu from './dropdown';

export default {
  mixins: [Emitter],
  name: 'historySelect',
  componentName: 'HistorySelect',
  components: {
    selectMenu
  },
  provide() {
    return {
      select: this
    };
  },
  props: {
    value: {
      required: true
    },
    name: String,
    id: String,
    placeholder: String,
    valueKey: {
      type: String,
      default: 'value'
    }
  },
  data() {
    return {
      options: [],
      optionsCount: 0,
      visible: false,
      selected: [],
      selectedLabel: '',
      cachedPlaceHolder: '',
      isSilentBlur: false,
      inputHovering: false,
      currentPlaceholder: '',
      previousQuery: null,
      softFocus: false,
      inputWidth: 0,
      hoverIndex: -1,
      menuVisibleOnFocus: false
    };
  },
  computed: {
    showClose() {
      let hasValue = Array.isArray(this.value) && this.value.length > 0;
      return this.inputHovering && hasValue;
    },
    iconClass() {
      return this.visible ? 'arrow-up is-reverse' : 'arrow-up';
    }
  },
  watch: {
    placeholder(val) {
      this.cachedPlaceHolder = this.currentPlaceholder = val;
    },
    value(val, oldVal) {
      this.resetInputHeight();
      if ((val && val.length > 0) || this.$refs.input) {
        this.currentPlaceholder = '';
      } else {
        this.currentPlaceholder = this.cachedPlaceHolder;
      }
      this.setSelected();
    },
    visible(val) {
      if (!val) {
        this.broadcast('selectDropdown', 'destroyPopper');
        if (this.$refs.input) {
          this.$refs.input.blur();
        }
        this.query = '';
        this.previousQuery = null;
        this.selectedLabel = '';
        this.inputLength = 20;
        this.menuVisibleOnFocus = false;
        this.resetHoverIndex();
        this.$nextTick(() => {
          if (this.$refs.input && this.$refs.input.value === '' && this.selected.length === 0) {
            this.currentPlaceholder = this.cachedPlaceHolder;
          }
        });
      } else {
        this.broadcast('selectDropdown', 'updatePopper');
      }
      this.$emit('visible-change', val);
    },
    options() {
      this.$nextTick(() => {
        this.broadcast('selectDropdown', 'updatePopper');
      });
      if (this.multiple) {
        this.resetInputHeight();
      }
      let inputs = this.$el.querySelectorAll('input');
      if ([].indexOf.call(inputs, document.activeElement) === -1) {
        this.setSelected();
      }
    }
  },
  directives: { clickOutside },
  methods: {
    handleClose() {
      this.visible = false;
    },
    toggleMenu() {
      if (this.menuVisibleOnFocus) {
        this.menuVisibleOnFocus = false;
      } else {
        this.visible = !this.visible;
      }
      if (this.visible) {
        (this.$refs.input || this.$refs.reference).focus();
      }
    },
    deleteTag(event, tag) {
      let index = this.selected.indexOf(tag);
      if (index > -1 && !this.selectDisabled) {
        const value = this.value.slice();
        value.splice(index, 1);
        this.$emit('input', value);
        this.emitChange(value);
        this.$emit('remove-tag', tag.value);
      }
      event.stopPropagation();
    },
    resetInputWidth() {
      this.inputWidth = this.$refs.reference.$el.getBoundingClientRect().width;
    },
    handleResize() {
      this.resetInputWidth();
      this.resetInputHeight();
    },
    resetInputHeight() {
      this.$nextTick(() => {
        if (!this.$refs.reference) return;
        let inputChildNodes = this.$refs.reference.$el.childNodes;
        let input = [].filter.call(inputChildNodes, item => item.tagName === 'INPUT')[0];
        const tags = this.$refs.tags;
        const sizeInMap = this.initialInputHeight || 40;
        input.style.height =
          this.selected.length === 0
            ? sizeInMap + 'px'
            : Math.max(tags ? tags.clientHeight + (tags.clientHeight > sizeInMap ? 6 : 0) : 0, sizeInMap) + 'px';
        if (this.visible && this.emptyText !== false) {
          this.broadcast('selectDropdown', 'updatePopper');
        }
      });
    },
    handleFocus(event) {
      if (!this.softFocus) {
        this.$emit('focus', event);
      } else {
        this.softFocus = false;
      }
    },
    handleBlur(event) {
      setTimeout(() => {
        if (this.isSilentBlur) {
          this.isSilentBlur = false;
        } else {
          this.$emit('blur', event);
        }
      }, 50);
      this.softFocus = false;
    },
    scrollToOption(option) {
      const target = Array.isArray(option) && option[0] ? option[0].$el : option.$el;
      if (this.$refs.popper && target) {
        const menu = this.$refs.popper.$el.querySelector('.el-select-dropdown__wrap');
        scrollIntoView(menu, target);
      }
      this.$refs.scrollbar && this.$refs.scrollbar.handleScroll();
    },
    setSelected() {
      let result = [];
      if (Array.isArray(this.value)) {
        this.value.forEach(value => {
          result.push(value);
        });
      }
      this.selected = result;
      this.$nextTick(() => {
        this.resetInputHeight();
      });
    },
    handleMenuEnter() {
      this.$nextTick(() => this.scrollToOption(this.selected));
    },
    doDestroy() {
      this.$refs.popper && this.$refs.popper.doDestroy();
    },
    resetHoverIndex() {
      setTimeout(() => {
        if (this.selected.length > 0) {
          this.hoverIndex = Math.min.apply(
            null,
            this.selected.map(item => this.options.indexOf(item))
          );
        } else {
          this.hoverIndex = -1;
        }
      }, 300);
    },
    getValueIndex(arr = [], value) {
      let index = -1;
      arr.some((item, i) => {
        if (item === value || item.startsWith(value)) {
          index = i;
          return true;
        }
        return false;
      });
      return index;
    },
    emitChange(val) {
      if (!valueEquals(this.value, val)) {
        this.$emit('change', val);
      }
    },
    handleOptionSelect(option, byClick) {
      const value = (this.value || []).slice();
      const optionIndex = this.getValueIndex(value, option.value);
      if (optionIndex > -1) {
        if (byClick) {
          value.splice(optionIndex, 1);
        } else {
          if (option.extraValue) {
            value[optionIndex] = option.value + option.extraValue;
          } else {
            value.splice(optionIndex, 1);
          }
        }
      } else {
        if (option.mode === 'normal') {
          value.push(option.value);
        } else {
          value.push(option.value + option.extraValue);
        }
      }

      this.$emit('input', value);
      this.emitChange(value);
      this.isSilentBlur = byClick;
      //   this.setSoftFocus();
      if (this.visible) return;
      this.$nextTick(() => {
        this.scrollToOption(option);
      });
    }
  },
  created() {
    this.cachedPlaceHolder = this.currentPlaceholder = this.placeholder;
    if (!Array.isArray(this.value)) {
      this.$emit('input', []);
    }

    this.$on('handleOptionClick', this.handleOptionSelect);
    this.$on('setSelected', this.setSelected);
  },
  mounted() {
    if (Array.isArray(this.value) && this.value.length > 0) {
      this.currentPlaceholder = '';
    }
    addResizeListener(this.$el, this.handleResize);

    const reference = this.$refs.reference;
    if (reference && reference.$el) {
      const sizeMap = {
        medium: 36,
        small: 32,
        mini: 28
      };
      const input = reference.$el.querySelector('input');
      this.initialInputHeight = input.getBoundingClientRect().height || sizeMap[this.selectSize];
    }
    this.resetInputHeight();
    this.$nextTick(() => {
      if (reference && reference.$el) {
        this.inputWidth = reference.$el.getBoundingClientRect().width;
      }
    });
    this.setSelected();
  },
  beforeDestroy() {
    if (this.$el && this.handleResize) removeResizeListener(this.$el, this.handleResize);
  }
};
</script>

<style lang="scss" scoped>
.history-input {
  width: 300px;
}
</style>
